import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Fingerprint, Lock, Radio } from "lucide-react";

const ITEMS = [
  { icon: ShieldCheck, title: "Licensed partners", body: "Regulated MSBs on both sides — FCA, DNB, CBK, CBN & FSCA." },
  { icon: Fingerprint, title: "Face-ID vault", body: "Local biometric unlock. Keys never leave your device." },
  { icon: Lock, title: "End-to-end encrypted", body: "AES-256 at rest, TLS 1.3 in flight. Zero-knowledge sync." },
  { icon: Radio, title: "24/7 watchtower", body: "ML fraud engine + human reviewers in every corridor timezone." },
];

export default function SecurityBand() {
  const { t } = useTranslation();
  return (
    <section className="py-24 md:py-32 bg-forest text-canvas relative noise overflow-hidden" data-testid="security-band">
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14">
          <div className="md:col-span-8">
            <div className="label-eyebrow !text-lime">{t("home.security.eyebrow")}</div>
            <h2 className="mt-4 font-display font-medium text-canvas text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest">
              {t("home.security.title")} <span className="italic font-serif text-lime">{t("home.security.italic")}</span>
            </h2>
          </div>
          <div className="md:col-span-4 text-canvas/70">
            Regulated where your money leaves and where it lands. Encrypted end-to-end. Reviewed by
            humans in every timezone.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
          {ITEMS.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.06, ease: [0.76, 0, 0.24, 1] }}
              className="rounded-3xl border border-canvas/12 p-8 bg-white/[0.03] backdrop-blur-sm"
              data-testid={`security-card-${i}`}
            >
              <div className="w-11 h-11 rounded-full bg-lime text-ink grid place-items-center">
                <it.icon className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <div className="mt-6 font-display text-2xl tracking-tight">{it.title}</div>
              <div className="mt-2 text-canvas/70 text-sm leading-relaxed">{it.body}</div>
            </motion.div>
          ))}
        </div>

        <div className="mt-16 flex flex-wrap items-center justify-between gap-6 pt-10 border-t border-canvas/15">
          <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-canvas/50">
            Regulatory partners
          </div>
          <div className="flex flex-wrap items-center gap-8 md:gap-12 text-canvas/85">
            {["FCA · UK", "DNB · NL", "CBN · NG", "CBK · KE", "FSCA · ZA", "BCEAO · WAEMU"].map((r) => (
              <span key={r} className="font-display text-lg tracking-tight">
                {r}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
